import {ContentTypeModel} from "@generated"
import {z} from "zod"

// Above this many inconsistencies of a single type, the dialog collapses them into one summary line
// so the window (and its buttons) can't grow off screen.
const INCONSISTENCY_SUMMARY_THRESHOLD = 10

const inconsistencySchema = z.object({
    message: z.string(),
    value: z.object({contentTypeModel: z.string()}).optional(),
})

// Plural noun used when collapsing many inconsistencies of one type into a summary line.
const inconsistencyTypeLabelsPlural: Partial<Record<string, string>> = {
    [ContentTypeModel.MaterialRevision]: "materials",
    [ContentTypeModel.Template]: "templates",
}

export const formatInconsistenciesMessage = (inconsistencies: readonly unknown[]): string => {
    // Group by content type, preserving first-seen order.
    const groupedMessagesByType = new Map<string, string[]>()
    for (const inconsistency of inconsistencies) {
        const parsed = inconsistencySchema.safeParse(inconsistency)
        if (!parsed.success) continue
        const type = parsed.data.value?.contentTypeModel ?? ""
        const messages = groupedMessagesByType.get(type) ?? []
        messages.push(parsed.data.message)
        groupedMessagesByType.set(type, messages)
    }

    const lines: string[] = []
    for (const [type, messages] of groupedMessagesByType) {
        const pluralLabel = inconsistencyTypeLabelsPlural[type]
        if (pluralLabel && messages.length > INCONSISTENCY_SUMMARY_THRESHOLD) {
            lines.push(`• ${messages.length} ${pluralLabel} belong to a different organization`)
        } else {
            lines.push(...messages.map((message) => `• ${message}`))
        }
    }

    return `This template will not work correctly due to the following issues:\n\n${lines.join("\n")}`
}
